import { type FC } from "react";
import {
  Box,
  Card,
  Checkbox,
  Grid,
  IconButton,
  Link as MuiLink,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useTranslation } from "react-i18next";
import type { ProductsItem } from "@/partner/entities/products";
import { useLiveEnabled } from "@/partner/entities/instance";
import { ProductStatusType } from "@/types/graphql/partner.generated";
import { useDeviceBreakpoint } from "@/partner/shared/hooks";
import { ItemTitleLink } from "@/partner/shared/components/ItemTitleLink";
import { PagePaths } from "@/partner/shared/constants";
import { ProductThumbnail } from "./ProductThumbnail";
import { getPriceButtonText } from "@/partner/features/products";

const TITLE_MAX_LENGTH = 200;
const TITLE_MAX_LENGTH_MOBILE = 60;

function truncate(text: string, maxLength: number): string {
  return text.length > maxLength ? `${text.slice(0, maxLength - 1)}…` : text;
}

interface ProductsListItemProps {
  product: ProductsItem;
  selected: boolean;
  onSelectOneChange: (
    id: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onClickItem: (id: string) => void;
}

// Порт референсного ProductListItem: чекбокс | превью | название+подпись
// цены | счётчики видео/стримов/коллекций (ссылки с фильтром по продукту) |
// публикация + переход на сайт.
export const ProductsListItem: FC<ProductsListItemProps> = ({
  product,
  selected,
  onSelectOneChange,
  onClickItem,
}) => {
  const { t } = useTranslation();
  const isMobile = useDeviceBreakpoint("md");
  const liveEnabled = useLiveEnabled();
  const priceText = getPriceButtonText(product, t);

  const publishAt = product.publishAt ? new Date(product.publishAt) : null;
  const isPublished = product.status !== ProductStatusType.DraftType;

  // «Зайти внутрь» = список нужного типа с фильтром по этому продукту
  // (референс: /videos?filter[productIds][]=id). Формат массива —
  // filter[...][]= (см. CollectionsListItem/FilterProvider.changeParams).
  const videosUrl = `${PagePaths.videos}?filter[productIds][]=${product.id}`;
  const streamsUrl = `${PagePaths.streams}?filter[productIds][]=${product.id}`;
  const collectionsUrl = `${PagePaths.collections}?filter[productIds][]=${product.id}`;

  return (
    <Card
      elevation={0}
      sx={{
        cursor: "pointer",
        opacity: isPublished ? 1 : 0.6,
        bgcolor: selected ? "background.selected" : "background.paper",
        p: { xs: 1.5, md: 2 },
        px: { xs: 0, md: 1 },
      }}
      onClick={() => onClickItem(product.id)}
    >
      <Grid
        container
        wrap="nowrap"
        spacing={isMobile ? 0 : 2}
        alignItems={isMobile ? "center" : "flex-start"}
      >
        <Grid>
          <Checkbox
            checked={selected}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => onSelectOneChange(product.id, e)}
          />
        </Grid>

        <Grid>
          <ProductThumbnail product={product} />
        </Grid>

        <Grid size="grow" sx={{ minWidth: 0 }}>
          <Box sx={{ ml: { xs: 1.5, md: 0 }, mr: { xs: 1.5, md: 0 } }}>
            <ItemTitleLink
              to={`${PagePaths.products}?tab=edit&id=${product.id}`}
              onOpen={() => onClickItem(product.id)}
              underline="hover"
              variant="h6"
              color="text.primary"
              sx={{
                display: "block",
                fontSize: { xs: "0.9rem", sm: "1.1rem", md: "1.2rem" },
              }}
            >
              {truncate(
                product.title,
                isMobile ? TITLE_MAX_LENGTH_MOBILE : TITLE_MAX_LENGTH
              )}
            </ItemTitleLink>
            {priceText && (
              <Typography
                variant="body2"
                color="text.secondary"
                display="block"
              >
                {priceText}
              </Typography>
            )}
          </Box>
        </Grid>

        {!isMobile && (
          <Grid>
            {/* Счётчик эфиров только там, где эфиры включены: на
                adult-инстансе раздел «Эфиры» скрыт целиком. */}
            {liveEnabled && product.streamsCount > 0 && (
              <div>
                <MuiLink
                  component={RouterLink}
                  to={streamsUrl}
                  variant="body2"
                  color="text.primary"
                  underline="hover"
                  display="block"
                  onClick={(e) => e.stopPropagation()}
                >
                  {t("products.item.streamsCount", {
                    count: product.streamsCount,
                  })}
                </MuiLink>
              </div>
            )}
            {product.videosCount > 0 && (
              <div>
                <MuiLink
                  component={RouterLink}
                  to={videosUrl}
                  variant="body2"
                  color="text.primary"
                  underline="hover"
                  display="block"
                  onClick={(e) => e.stopPropagation()}
                >
                  {t("products.item.videosCount", {
                    count: product.videosCount,
                  })}
                </MuiLink>
              </div>
            )}
            {product.collectionsCount > 0 && (
              <div>
                <MuiLink
                  component={RouterLink}
                  to={collectionsUrl}
                  variant="body2"
                  color="text.primary"
                  underline="hover"
                  display="block"
                  onClick={(e) => e.stopPropagation()}
                >
                  {t("products.item.collectionsCount", {
                    count: product.collectionsCount,
                  })}
                </MuiLink>
              </div>
            )}
          </Grid>
        )}

        {!isMobile && (
          <Grid size={3}>
            <Stack direction="column" alignItems="flex-end">
              <Box>
                {product.status === ProductStatusType.FinishedType && (
                  <>
                    <Typography
                      variant="body2"
                      color="text.primary"
                      align="right"
                    >
                      {t("products.item.finishedAt", {
                        date: product.finishAt,
                      })}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      align="right"
                    >
                      {t("products.item.finishedAtDay", {
                        date: product.finishAt,
                      })}
                    </Typography>
                  </>
                )}

                {product.status === ProductStatusType.PublishedType &&
                  publishAt && (
                    <>
                      <Typography
                        variant="body2"
                        color="text.primary"
                        align="right"
                      >
                        {publishAt > new Date()
                          ? t("products.item.willBePublishedAt", {
                              date: product.publishAt,
                            })
                          : t("products.item.publishedAt", {
                              date: product.publishAt,
                            })}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        align="right"
                      >
                        {t("products.item.publishedAtDay", {
                          date: product.publishAt,
                        })}
                      </Typography>
                    </>
                  )}

                {product.status === ProductStatusType.DraftType && (
                  <Typography
                    variant="body2"
                    color="text.primary"
                    align="right"
                  >
                    {t("products.item.notPublished")}
                  </Typography>
                )}
              </Box>

              {isPublished && (
                <Box>
                  <Tooltip title={t("products.item.openTooltip")}>
                    <IconButton
                      target="_blank"
                      href={product.siteUrl}
                      size="large"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <OpenInNewIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Stack>
          </Grid>
        )}
      </Grid>
    </Card>
  );
};
